/**
 * AuthLayout — shared chrome for the Clerk auth surface.
 *
 * Wraps Clerk's prebuilt `<SignUp />` (at `/`, the default) or
 * `<SignIn />` (at `/sign-in`) in identical full-viewport gradient
 * + XP `.window` chrome so both routes look the same except for the
 * title-bar text. The Clerk component owns everything inside
 * `{children}` — including social buttons, email/password fields, and
 * the bottom cross-link that bounces users to the sibling route.
 *
 * Per the project's CLAUDE.md "Repancy Clerk auth surface" rule: this
 * layout is *only* a thin wrapper; do NOT add interactive logic here.
 * Auth flow logic lives in Clerk's prebuilt component, not in app
 * code. If a future change requires custom-flow hooks (useSignIn /
 * useSignUp), confirm with the user first.
 */
import type { ReactNode } from 'react';
import { testIds } from '../../lib/testIds';
import { FloatingIcons } from './FloatingIcons';

interface AuthLayoutProps {
  title: string;
  children: ReactNode;
}

export function AuthLayout({ title, children }: AuthLayoutProps) {
  return (
    <main
      data-testid={testIds.auth.signInPage}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background:
          'linear-gradient(135deg, #0a0a0a 0%, #111118 50%, #1a1a2e 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
        boxSizing: 'border-box',
        fontFamily: 'var(--xp-font-body)',
        overflowY: 'auto',
      }}
    >
      <FloatingIcons />
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 14,
          maxWidth: 'calc(100vw - 40px)',
        }}
      >
      <div
        className="window"
        style={{
          width: 'fit-content',
          maxWidth: '100%',
          maxHeight: 'calc(100vh - 80px)',
          display: 'flex',
          flexDirection: 'column',
          borderBottomLeftRadius: 8,
          borderBottomRightRadius: 8,
          overflow: 'hidden',
        }}
      >
        <div className="title-bar" style={{ flexShrink: 0, userSelect: 'none' }}>
          <div className="title-bar-text" style={{ flex: 1 }}>
            {title}
          </div>
        </div>
        <div
          className="window-body"
          style={{
            margin: 0,
            padding: 0,
            display: 'flex',
            justifyContent: 'center',
            overflowY: 'auto',
            fontFamily: 'var(--xp-font-body)',
            background: '#f2f2f2',
          }}
        >
          {children}
        </div>
      </div>
        <footer
          data-testid={testIds.auth.signInFooter}
          style={{
            color: 'rgba(255, 255, 255, 0.72)',
            fontSize: 11,
            textAlign: 'center',
            lineHeight: 1.4,
          }}
        >
          © 2026 Arewe Inc. ·{' '}
          <a
            href="mailto:hello@arewe.ai"
            style={{ color: 'rgba(255, 255, 255, 0.92)', textDecoration: 'underline' }}
          >
            hello@arewe.ai
          </a>
        </footer>
      </div>
    </main>
  );
}
